// Menu khu Quản trị NPP (docs/KE_HOACH_NPP_SANG_WEB.md §4.3). Mỗi mục hiện khi tài khoản có ít nhất một quyền
// mà API của trang đó cần — cùng danh sách với trang quản trị cũ (apps/admin/src/lib/permissions.ts
// ROUTE_PERMISSIONS), để sub-admin chỉ thấy đúng mục được tick.

import {
  Activity, ArrowUpCircle, ChartColumn, ClipboardCheck, Coins, FileSearch, FileSpreadsheet, HandCoins, Layers,
  LayoutDashboard, LifeBuoy, Megaphone, Network, PackageOpen, ReceiptText, Settings2, ShieldCheck, UserRound, UserX, Users,
  type LucideIcon,
} from 'lucide-react'
import type { Tone } from '@/components/ui/kpi-tile'
import type { AdminPermission } from '@/lib/nppAdmin'

export const NPP_ROUTE_PERMISSIONS = {
  dashboard: ['dashboard.view'],
  units: ['units.view'],
  upgrades: ['units.view'],
  announcements: ['announce.send', 'admins.manage'],
  orders: ['orders.view'],
  priceLists: ['pricing.cost.view', 'pricing.lists.manage', 'pricing.assign'],
  commissions: ['commission.manage', 'commission.close', 'finance.view'],
  carrierStatements: ['reconcile.view', 'reconcile.carrier'],
  payouts: ['reconcile.view', 'payout.create', 'payout.approve', 'payout.pay', 'payout.cancel', 'finance.view'],
  debts: ['debt.manage', 'finance.view', 'reconcile.view', 'finance.writeoff'],
  orderApprovals: ['reconcile.ship_prepay'],
  accountDeletions: ['units.agency.manage', 'units.ctv.manage', 'units.shop.manage'],
  tickets: ['tickets.view', 'tickets.handle'],
  finance: ['finance.view'],
  settings: ['settings.manage', 'finance.view'],
  masterSettings: ['settings.manage', 'carrier.view', 'carrier.manage'],
  settingsPage: ['settings.manage', 'finance.view', 'carrier.view', 'carrier.manage'],
  admins: ['admins.manage'],
  audit: ['audit.view'],
  sagas: ['sagas.view'],
} as const satisfies Record<string, readonly AdminPermission[]>

export interface NppNavItem {
  to: string
  label: string
  icon: LucideIcon
  tone: Tone
  anyOf: readonly string[]
  group?: string
  /** Đường dẫn tương ứng ở trang quản trị cũ (/admin-console) — màn chưa chuyển xong thì mở ở đó. */
  legacy: string
}

export const NPP_NAV: NppNavItem[] = [
  { to: '/npp', label: 'Tổng quan', icon: LayoutDashboard, tone: 'blue', anyOf: NPP_ROUTE_PERMISSIONS.dashboard, legacy: '/' },

  { to: '/npp/network', label: 'Hệ thống', icon: Network, tone: 'blue', anyOf: NPP_ROUTE_PERMISSIONS.units, group: 'Mạng lưới', legacy: '/network' },
  // Người dùng: đơn vị (Shop / CTV / Đại lý) + chủ tài khoản — gộp trang Tài khoản cũ (/npp/users chuyển về đây)
  { to: '/npp/units', label: 'Người dùng', icon: Users, tone: 'blue', anyOf: NPP_ROUTE_PERMISSIONS.units, group: 'Mạng lưới', legacy: '/shops' },
  { to: '/npp/upgrades', label: 'Nâng cấp', icon: ArrowUpCircle, tone: 'blue', anyOf: NPP_ROUTE_PERMISSIONS.upgrades, group: 'Mạng lưới', legacy: '/upgrades' },
  { to: '/npp/announcements', label: 'Gửi thông báo', icon: Megaphone, tone: 'blue', anyOf: NPP_ROUTE_PERMISSIONS.announcements, group: 'Mạng lưới', legacy: '/announcements' },
  { to: '/npp/account-deletions', label: 'Xóa tài khoản', icon: UserX, tone: 'gray', anyOf: NPP_ROUTE_PERMISSIONS.accountDeletions, group: 'Mạng lưới', legacy: '/account-deletions' },

  { to: '/npp/orders', label: 'Đơn hàng', icon: PackageOpen, tone: 'green', anyOf: NPP_ROUTE_PERMISSIONS.orders, group: 'Đơn hàng', legacy: '/orders' },
  { to: '/npp/order-approvals', label: 'Duyệt đơn', icon: ClipboardCheck, tone: 'green', anyOf: NPP_ROUTE_PERMISSIONS.orderApprovals, group: 'Đơn hàng', legacy: '/order-approvals' },
  { to: '/npp/tickets', label: 'Góp ý', icon: LifeBuoy, tone: 'green', anyOf: NPP_ROUTE_PERMISSIONS.tickets, group: 'Đơn hàng', legacy: '/tickets' },

  { to: '/npp/price-lists', label: 'Bảng giá', icon: Layers, tone: 'violet', anyOf: NPP_ROUTE_PERMISSIONS.priceLists, group: 'Giá & chiết khấu', legacy: '/price-lists' },
  { to: '/npp/commissions', label: 'Chiết khấu', icon: Coins, tone: 'violet', anyOf: NPP_ROUTE_PERMISSIONS.commissions, group: 'Giá & chiết khấu', legacy: '/commissions' },

  { to: '/npp/carrier-statements', label: 'COD & Đối soát', icon: FileSpreadsheet, tone: 'orange', anyOf: NPP_ROUTE_PERMISSIONS.carrierStatements, group: 'Đối soát', legacy: '/carrier-statements' },
  { to: '/npp/payouts', label: 'Chi trả', icon: HandCoins, tone: 'orange', anyOf: NPP_ROUTE_PERMISSIONS.payouts, group: 'Đối soát', legacy: '/payouts' },
  { to: '/npp/debts', label: 'Công nợ', icon: ReceiptText, tone: 'orange', anyOf: NPP_ROUTE_PERMISSIONS.debts, group: 'Đối soát', legacy: '/debts' },
  { to: '/npp/finance', label: 'Tổng quan tài chính', icon: ChartColumn, tone: 'orange', anyOf: NPP_ROUTE_PERMISSIONS.finance, group: 'Đối soát', legacy: '/finance' },

  { to: '/npp/settings', label: 'Cài đặt', icon: Settings2, tone: 'gray', anyOf: NPP_ROUTE_PERMISSIONS.settingsPage, group: 'Hệ thống', legacy: '/settings' },
  { to: '/npp/admins', label: 'Quản trị viên', icon: ShieldCheck, tone: 'gray', anyOf: NPP_ROUTE_PERMISSIONS.admins, group: 'Hệ thống', legacy: '/admins' },
  { to: '/npp/sagas', label: 'Tác vụ', icon: Activity, tone: 'gray', anyOf: NPP_ROUTE_PERMISSIONS.sagas, group: 'Hệ thống', legacy: '/sagas' },
  { to: '/npp/audit', label: 'Nhật ký', icon: FileSearch, tone: 'gray', anyOf: NPP_ROUTE_PERMISSIONS.audit, group: 'Hệ thống', legacy: '/audit' },
  { to: '/npp/account', label: 'Tài khoản', icon: UserRound, tone: 'gray', anyOf: [], group: 'Hệ thống', legacy: '/account' },
]

/** Thanh đáy điện thoại: 4 ô hay dùng (ô nào không có quyền thì bỏ) + "Thêm"/menu. */
export const NPP_BOTTOM_PATHS = ['/npp', '/npp/network', '/npp/orders', '/npp/payouts']
/** Nhãn gọn cho ô thanh đáy (mặc định lấy nhãn menu). */
export const NPP_BOTTOM_LABEL: Record<string, string> = { '/npp/network': 'Hệ thống', '/npp/payouts': 'Chi trả' }

/** Đường dẫn trang quản trị cũ của một mục (khi màn đó chưa chuyển sang web). */
export function legacyUrlOf(to: string): string {
  const item = NPP_NAV.find((i) => i.to === to)
  return `/admin-console${item?.legacy ?? '/'}`
}

/**
 * Đổi đường dẫn của trang quản trị cũ sang khu NPP của web — dùng khi chuyển màn có link nội bộ
 * (ví dụ `/shops?status=active` → `/npp/units?status=active`, `/settings?tab=best` → `/npp/settings?tab=best`).
 */
export function toNpp(legacyPath: string): string {
  const [pathname, query = ''] = legacyPath.split('?')
  let best: NppNavItem | null = null
  for (const i of NPP_NAV) {
    if (i.legacy === '/') continue
    if (pathname === i.legacy || pathname!.startsWith(i.legacy + '/')) {
      if (!best || i.legacy.length > best.legacy.length) best = i
    }
  }
  const mapped = best ? best.to + pathname!.slice(best.legacy.length) : pathname === '/' ? '/npp' : `/npp${pathname}`
  return query ? `${mapped}?${query}` : mapped
}
